import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "@/lib/api";
import { HERO } from "@/constants/testIds";

const line = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.12, duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.9 + i * 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ onCta }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const banyanY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const paperY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const [stats, setStats] = useState({ total_trees: 3421, states_covered: 12, members_recorded: 128940 });

  useEffect(() => {
    api.get("/stats").then((r) => setStats((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={HERO.root}
      className="grain relative min-h-[100svh] pt-32 pb-24 overflow-hidden"
    >
      {/* Vintage paper texture layer */}
      <motion.img
        aria-hidden
        style={{ y: paperY }}
        src="https://images.unsplash.com/photo-1705837863332-7162639852d8?crop=entropy&cs=srgb&fm=jpg&w=1400&q=60"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.18] mix-blend-multiply pointer-events-none"
        alt=""
      />
      {/* Banyan silhouette */}
      <motion.img
        aria-hidden
        style={{ y: banyanY }}
        src="https://images.unsplash.com/photo-1783768272434-cd6d2ea34a2a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=70"
        className="absolute -right-40 top-24 w-[900px] max-w-none opacity-[0.09] mix-blend-multiply pointer-events-none"
        alt=""
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 items-end">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          className="col-span-12 flex items-center gap-4"
        >
          <span className="h-px w-16 bg-ink/40" />
          <span className="text-xs uppercase tracking-[0.3em] text-ink-50">
            A living archive · Est. in every Indian home
          </span>
        </motion.div>

        {/* Headline (masked line reveal) */}
        <div className="col-span-12 lg:col-span-8 mt-4" data-testid={HERO.headline}>
          <h1 className="font-serif tracking-tighter leading-[0.92] text-ink text-[3rem] sm:text-[4.25rem] md:text-[5.5rem] lg:text-[7.2rem]">
            {[
              { text: "Discover", italic: false, color: false },
              { text: "your family", italic: true, color: true },
              { text: "story.", italic: false, color: false },
            ].map((row, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className={`inline-block will-change-transform pb-2 ${row.italic ? "italic" : ""} ${row.color ? "text-saffron" : ""}`}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  custom={i}
                >
                  {row.text}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        {/* Portrait card, right column */}
        <motion.div
          style={{ y: portraitY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-4 lg:mt-24"
        >
          <div className="relative w-[220px] sm:w-[260px] lg:w-full lg:max-w-[320px] mx-auto lg:ml-auto">
            <div className="arch spotlight border border-ivory-300 shadow-[0_20px_80px_-30px_rgba(10,15,13,0.45)]">
              <img
                src="https://images.unsplash.com/photo-1732303105237-f5b5f7a2390e?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                alt="A vintage Indian family portrait"
                className="w-full h-[380px] lg:h-[440px] object-cover grayscale-[0.15] sepia-[0.15]"
              />
            </div>
            <div className="mt-3 flex items-center justify-between font-serif italic text-ink-50 text-sm">
              <span>The Iyer family</span>
              <span>Thanjavur · 1962</span>
            </div>
            {/* Corner marks */}
            <span className="absolute -left-2 -top-2 w-4 h-4 border-l border-t border-ink/60" />
            <span className="absolute -right-2 -top-2 w-4 h-4 border-r border-t border-ink/60" />
          </div>
        </motion.div>

        {/* Sub + CTAs */}
        <motion.p
          variants={fade}
          initial="hidden"
          animate="show"
          custom={0}
          data-testid={HERO.sub}
          className="col-span-12 lg:col-span-7 mt-10 text-lg sm:text-xl text-ink-50 font-light leading-relaxed max-w-2xl"
        >
          Build your family tree, preserve generations of memories, explore ancestral villages,
          and reconnect with heritage — from a Kalpathy tharavad to a Toronto suburb.
        </motion.p>

        <motion.div
          variants={fade}
          initial="hidden"
          animate="show"
          custom={1}
          className="col-span-12 mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={onCta}
            data-testid={HERO.ctaStartTree}
            className="group relative rounded-full bg-saffron text-ivory px-7 py-4 text-sm font-medium tracking-wide hover:bg-saffron-deep active:scale-[0.98] transition"
          >
            Start your family tree
            <span className="ml-1 inline-block group-hover:translate-x-1 transition-transform">→</span>
          </button>
          <a
            href="#records"
            data-testid={HERO.ctaSearchRecords}
            className="rounded-full border border-ink/25 text-ink px-7 py-4 text-sm font-medium tracking-wide hover:border-ink hover:bg-ink hover:text-ivory transition"
          >
            Search family records
          </a>
          <a
            href="#heritage"
            data-testid={HERO.ctaExploreHeritage}
            className="text-sm font-medium tracking-wide text-ink-50 hover:text-ink ink-link"
          >
            Explore Indian heritage →
          </a>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="show"
          custom={2}
          className="col-span-12 mt-16 grid grid-cols-3 gap-6 border-t border-ivory-300 pt-8"
        >
          <div data-testid={HERO.statTrees}>
            <div className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">
              {stats.total_trees.toLocaleString()}
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-ink-50 mt-1">Indian trees</div>
          </div>
          <div data-testid={HERO.statStates}>
            <div className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">
              {stats.states_covered}
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-ink-50 mt-1">States covered</div>
          </div>
          <div data-testid={HERO.statMembers}>
            <div className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">
              {stats.members_recorded.toLocaleString()}
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-ink-50 mt-1">Members recorded</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
